import React, { useState, useEffect } from 'react';
import { X, Plus, Save, Trash2, Loader2, GripVertical } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { ScorecardCriterion } from '../types';

interface ScorecardConfigModalProps {
  vacancyId: string;
  onClose: () => void;
}

export default function ScorecardConfigModal({ vacancyId, onClose }: ScorecardConfigModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stageId, setStageId] = useState<string | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [criteria, setCriteria] = useState<ScorecardCriterion[]>([]);

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        // Find Interview stage for this vacancy
        const { data: stages } = await supabase
          .from('vacancy_stages')
          .select('*')
          .eq('vacancy_id', vacancyId)
          .ilike('name', '%Interview%')
          .limit(1);

        let activeStageId = null;
        if (stages && stages.length > 0) {
          activeStageId = stages[0].id;
          setStageId(activeStageId);
        } else {
          // Fallback if no specific "Interview" stage found by name, just get any stage
          const { data: anyStages } = await supabase
            .from('vacancy_stages')
            .select('*')
            .eq('vacancy_id', vacancyId)
            .limit(1);
          if (anyStages && anyStages.length > 0) {
            activeStageId = anyStages[0].id;
            setStageId(activeStageId);
          }
        }

        if (activeStageId) {
          const { data: form } = await supabase
            .from('feedback_forms')
            .select('*')
            .eq('stage_id', activeStageId)
            .single();

          if (form && form.criteria) {
            setFormId(form.id);
            setCriteria(form.criteria);
          } else {
            // Default criteria
            setCriteria([
              { id: Date.now().toString(), category: 'Vakinhoudelijke Kennis', weight: 5, questions: ['Wat is de relevante werkervaring?'] },
              { id: (Date.now() + 1).toString(), category: 'Motivatie & Cultuurfit', weight: 4, questions: ['Waarom wil de kandidaat hier werken?'] }
            ]);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [vacancyId]);

  const handleAddCriterion = () => {
    setCriteria([
      ...criteria,
      { id: Date.now().toString(), category: 'Nieuwe Competentie', weight: 3, questions: ['Start met een voorbeeldvraag...'] }
    ]);
  };

  const handleUpdateCriterion = (index: number, field: keyof ScorecardCriterion, value: any) => {
    const newCriteria = [...criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setCriteria(newCriteria);
  };

  const handleUpdateQuestion = (cIndex: number, qIndex: number, value: string) => {
    const newCriteria = [...criteria];
    newCriteria[cIndex].questions[qIndex] = value;
    setCriteria(newCriteria);
  };

  const handleAddQuestion = (cIndex: number) => {
    const newCriteria = [...criteria];
    newCriteria[cIndex].questions.push('');
    setCriteria(newCriteria);
  };

  const handleRemoveQuestion = (cIndex: number, qIndex: number) => {
    const newCriteria = [...criteria];
    newCriteria[cIndex].questions.splice(qIndex, 1);
    setCriteria(newCriteria);
  };

  const handleRemoveCriterion = (index: number) => {
    const newCriteria = [...criteria];
    newCriteria.splice(index, 1);
    setCriteria(newCriteria);
  };

  const handleSave = async () => {
    if (!stageId) {
      alert("Geen interview fase gevonden voor deze vacature.");
      return;
    }
    setSaving(true);
    try {
      if (formId) {
        await supabase
          .from('feedback_forms')
          .update({ criteria })
          .eq('id', formId);
      } else {
        const { data } = await supabase
          .from('feedback_forms')
          .insert({ stage_id: stageId, criteria })
          .select()
          .single();
        if (data) setFormId(data.id);
      }
      onClose();
    } catch (e) {
      console.error(e);
      alert("Fout bij opslaan van scorecard.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-fadeIn p-4 sm:p-6 p-safe">
      <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] animate-scaleIn">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
          <div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">Scorecard Configureren</h2>
            <p className="text-slate-500 text-xs font-medium">Beheer de beoordelingscriteria voor deze vacature.</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-white custom-scrollbar">
          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="animate-spin text-slate-300" size={32} /></div>
          ) : (
            <div className="space-y-6">
              {criteria.map((c, cIndex) => (
                <div key={c.id} className="p-5 rounded-2xl border border-slate-200 bg-slate-50 relative group">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button onClick={() => handleRemoveCriterion(cIndex)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-white rounded-lg shadow-sm">
                      <Trash2 size={16} />
                    </button>
                    <div className="p-1.5 text-slate-400 cursor-move">
                      <GripVertical size={16} />
                    </div>
                  </div>

                  <div className="space-y-4 max-w-xl">
                    <div>
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block mb-1">Competentie</label>
                      <input 
                        value={c.category}
                        onChange={(e) => handleUpdateCriterion(cIndex, 'category', e.target.value)}
                        className="w-full bg-white border border-slate-200 text-sm font-bold text-slate-800 rounded-xl px-4 py-2 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block mb-1">Weging (1-5)</label>
                      <input 
                        type="number" 
                        min="1" max="5"
                        value={c.weight}
                        onChange={(e) => handleUpdateCriterion(cIndex, 'weight', parseInt(e.target.value))}
                        className="w-24 bg-white border border-slate-200 text-sm font-bold text-slate-800 rounded-xl px-4 py-2 outline-none focus:border-red-500"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block mb-2">Evaluatie Vragen</label>
                      <div className="space-y-2">
                        {c.questions.map((q, qIndex) => (
                          <div key={qIndex} className="flex items-center gap-2">
                            <input 
                              value={q}
                              onChange={(e) => handleUpdateQuestion(cIndex, qIndex, e.target.value)}
                              className="flex-1 bg-white border border-slate-200 text-sm text-slate-600 rounded-lg px-3 py-1.5 outline-none focus:border-red-500"
                              placeholder="Bijv: Kan de kandidaat goed zelfstandig werken?"
                            />
                            <button onClick={() => handleRemoveQuestion(cIndex, qIndex)} className="text-slate-300 hover:text-red-500 p-1">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button onClick={() => handleAddQuestion(cIndex)} className="mt-3 text-[10px] font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1">
                        <Plus size={12} /> Vraag Toevoegen
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button onClick={handleAddCriterion} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-500 font-bold hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center justify-center gap-2">
                <Plus size={18} /> Nieuwe Competentie
              </button>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-white flex justify-end shrink-0">
          <button 
            onClick={handleSave} 
            disabled={loading || saving}
            className="bg-slate-900 text-white px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-black transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Scorecard Opslaan
          </button>
        </div>
      </div>
    </div>
  );
}
